import React from 'react';
import { Box, Chip, IconButton, TextField, Tooltip, Typography } from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import StarIcon from '@mui/icons-material/Star';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import BoltIcon from '@mui/icons-material/Bolt';
import { PsfColors } from '../theme';

// ── Gamified building blocks for the Personalized PSF criteria panels ──────
// Accents passed in here must be 6-digit hex: alpha tints are made by
// appending two hex digits (`${accent}22`).

export type RangeValue = { min: string; max: string };
export type RangePreset = { label: string; min?: string; max?: string };

const sameBound = (a: string, b?: string) => a.replace(/,/g, '') === (b ?? '').replace(/,/g, '');
const presetActive = (value: RangeValue, preset: RangePreset) =>
  sameBound(value.min, preset.min) && sameBound(value.max, preset.max);

export const rangeIsSet = (value: RangeValue) => Boolean(value.min || value.max);

/** A numbered "quest" card that lights up once any of its criteria are set. */
export function QuestCard({
  step, icon, title, hint, accent, active, badge, onReset, children, c, sx,
}: {
  step: number;
  icon: React.ReactNode;
  title: string;
  hint?: string;
  accent: string;
  active: boolean;
  badge?: string;
  onReset?: () => void;
  children: React.ReactNode;
  c: PsfColors;
  sx?: object;
}) {
  return (
    <Box sx={{
      position: 'relative', minWidth: 0, borderRadius: 2, p: 1.1,
      border: `1.5px solid ${active ? accent : c.border}`,
      background: active
        ? `linear-gradient(160deg, ${accent}1f 0%, ${accent}05 55%)`
        : `linear-gradient(160deg, ${accent}0b 0%, transparent 45%)`,
      boxShadow: active ? `0 0 0 3px ${accent}14, 0 6px 18px -10px ${accent}` : 'none',
      transition: 'border-color 160ms, box-shadow 160ms, background 160ms',
      ...sx,
    }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.85, mb: 1 }}>
        <Box sx={{
          position: 'relative', width: 30, height: 30, flexShrink: 0, borderRadius: '50%',
          display: 'grid', placeItems: 'center', color: '#fff',
          background: `linear-gradient(135deg, ${accent}, ${accent}aa)`,
          boxShadow: `0 3px 10px -3px ${accent}`,
          '& svg': { fontSize: 17 },
        }}>
          {icon}
          <Box sx={{
            position: 'absolute', right: -5, bottom: -4, minWidth: 15, height: 15, px: 0.3,
            borderRadius: 8, bgcolor: c.surfaceCard, border: `1.5px solid ${accent}`,
            color: accent, fontSize: '0.55rem', fontWeight: 900, lineHeight: '12px', textAlign: 'center',
          }}>
            {step}
          </Box>
        </Box>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6, flexWrap: 'wrap' }}>
            <Typography sx={{ color: c.text, fontWeight: 800, fontSize: '0.78rem', lineHeight: 1.2 }}>
              {title}
            </Typography>
            {badge && (
              <Chip
                size="small"
                icon={<StarIcon sx={{ fontSize: '11px !important', color: `${c.navyDark} !important` }} />}
                label={badge}
                sx={{
                  height: 16, fontSize: '0.54rem', fontWeight: 900, letterSpacing: 0.4,
                  bgcolor: c.yellow, color: c.navyDark, '& .MuiChip-label': { px: 0.6 },
                }}
              />
            )}
          </Box>
          {hint && (
            <Typography sx={{ color: c.textMuted, fontSize: '0.62rem', lineHeight: 1.25 }} noWrap>
              {hint}
            </Typography>
          )}
        </Box>
        {active ? (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.25 }}>
            <Chip
              size="small"
              icon={<CheckCircleIcon sx={{ fontSize: '12px !important', color: `${accent} !important` }} />}
              label="Set"
              sx={{ height: 18, fontSize: '0.6rem', fontWeight: 800, bgcolor: `${accent}22`, color: accent }}
            />
            {onReset && (
              <Tooltip title={`Reset ${title}`}>
                <IconButton size="small" onClick={onReset} aria-label={`Reset ${title}`} sx={{ color: c.textMuted, p: 0.3 }}>
                  <RestartAltIcon sx={{ fontSize: 15 }} />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        ) : (
          <Typography sx={{ color: c.textMuted, fontSize: '0.58rem', fontWeight: 700, letterSpacing: 0.4, opacity: 0.8 }}>
            OPTIONAL
          </Typography>
        )}
      </Box>
      {children}
    </Box>
  );
}

const POWER_TIERS = ['Wide Net', 'Scout', 'Tracker', 'Hunter', 'Sharpshooter', 'Bullseye'] as const;

/** Five-segment meter: every criterion set lights one segment. */
export function PowerMeter({ count, c }: { count: number; c: PsfColors }) {
  const lit = Math.min(5, count);
  return (
    <Tooltip title={`${count} search ${count === 1 ? 'criterion' : 'criteria'} set — each one sharpens your aim`}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6, minWidth: 0 }}>
        <BoltIcon sx={{ fontSize: 15, color: lit ? c.yellow : c.textMuted }} />
        <Box sx={{ display: 'flex', gap: '3px' }}>
          {Array.from({ length: 5 }, (_, index) => (
            <Box
              key={index}
              sx={{
                width: 12, height: 7, borderRadius: '2px',
                transform: 'skewX(-18deg)',
                bgcolor: index < lit ? (index >= 3 ? c.orange : c.yellow) : 'transparent',
                border: `1px solid ${index < lit ? 'transparent' : c.border}`,
                boxShadow: index < lit ? `0 0 6px ${index >= 3 ? c.orange : c.yellow}88` : 'none',
                transition: 'background-color 200ms, box-shadow 200ms',
              }}
            />
          ))}
        </Box>
        <Typography sx={{
          fontSize: '0.62rem', fontWeight: 800, letterSpacing: 0.3, whiteSpace: 'nowrap',
          color: lit ? (lit >= 4 ? c.orange : c.text) : c.textMuted,
        }}>
          {POWER_TIERS[lit]}
        </Typography>
      </Box>
    </Tooltip>
  );
}

/** Two-option-or-more pill selector with optional icons. */
export function PillSelect<T extends string>({
  value, options, onChange, accent, c, size = 'md',
}: {
  value: T;
  options: Array<{ value: T; label: string; icon?: React.ReactNode }>;
  onChange: (value: T) => void;
  accent: string;
  c: PsfColors;
  size?: 'md' | 'lg' | 'xl';
}) {
  const lg = size === 'lg';
  const xl = size === 'xl';
  return (
    <Box sx={{ display: 'flex', gap: 0.5 }}>
      {options.map((option) => {
        const selected = option.value === value;
        return (
          <Box
            key={option.value}
            role="button"
            tabIndex={0}
            aria-pressed={selected}
            onClick={() => onChange(option.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter' || event.key === ' ') { event.preventDefault(); onChange(option.value); }
            }}
            sx={{
              flex: 1, minWidth: 0, px: xl ? 1.6 : lg ? 1.25 : 0.75, py: xl ? 1.35 : lg ? 1.1 : 0.55, borderRadius: lg || xl ? 2 : 1.5, cursor: 'pointer', userSelect: 'none',
              display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5,
              border: `1.5px solid ${selected ? accent : c.border}`,
              bgcolor: selected ? `${accent}22` : 'transparent',
              color: selected ? accent : c.textMuted,
              fontSize: xl ? '0.98rem' : lg ? '0.86rem' : '0.7rem', fontWeight: selected ? 800 : 600, whiteSpace: 'nowrap',
              boxShadow: selected && (lg || xl) ? `0 0 0 3px ${accent}1f` : 'none',
              transition: 'all 140ms',
              '&:hover': { borderColor: accent },
              '& svg': { fontSize: xl ? 22 : lg ? 19 : 15 },
            }}
          >
            {option.icon}
            {option.label}
          </Box>
        );
      })}
    </Box>
  );
}

/** On/off chip — a lit "power-up" when on. */
export function ToggleChip({
  label, icon, on, onToggle, accent, c, adornment, size = 'md',
}: {
  label: string;
  icon: React.ReactElement;
  on: boolean;
  onToggle: () => void;
  accent: string;
  c: PsfColors;
  adornment?: React.ReactNode;
  size?: 'md' | 'lg' | 'xl';
}) {
  const lg = size === 'lg';
  const xl = size === 'xl';
  return (
    <Box sx={{ display: 'inline-flex', alignItems: 'center' }}>
      <Chip
        size="small"
        icon={icon}
        label={label}
        onClick={onToggle}
        aria-pressed={on}
        variant={on ? 'filled' : 'outlined'}
        sx={{
          height: xl ? 40 : lg ? 34 : 24, fontSize: xl ? '0.9rem' : lg ? '0.8rem' : '0.66rem', fontWeight: on ? 800 : 600, px: xl || lg ? 0.6 : 0,
          color: on ? '#fff' : c.textMuted,
          bgcolor: on ? accent : 'transparent',
          borderColor: on ? accent : c.border,
          '& .MuiChip-icon': { fontSize: xl ? 21 : lg ? 18 : 14, color: on ? '#fff' : c.textMuted },
          '&:hover': { bgcolor: on ? accent : `${accent}18` },
          '&.MuiChip-clickable:hover': { bgcolor: on ? accent : `${accent}18` },
        }}
      />
      {adornment}
    </Box>
  );
}

/** Min–max range with quick-pick preset chips. */
export function RangeStat({
  label, value, onChange, money = false, presets, accent, c, emphasis = false,
}: {
  label: string;
  value: RangeValue;
  onChange: (value: RangeValue) => void;
  money?: boolean;
  presets?: RangePreset[];
  accent: string;
  c: PsfColors;
  emphasis?: boolean | 'xl';
}) {
  const set = rangeIsSet(value);
  const xl = emphasis === 'xl';
  const large = Boolean(emphasis);
  const format = (raw: string) => {
    if (!money) return raw;
    const digits = raw.replace(/[^\d]/g, '');
    return digits ? Number(digits).toLocaleString('en-SG') : '';
  };
  const fieldSx = {
    flex: 1, minWidth: 0,
    '& .MuiOutlinedInput-root': { bgcolor: c.surfaceCard },
    '& input': { color: c.text, fontSize: xl ? '0.94rem' : large ? '0.82rem' : '0.72rem', py: xl ? 1 : large ? 0.75 : 0.5, px: xl ? 1 : 0.75 },
    '& .MuiOutlinedInput-notchedOutline': { borderColor: set ? `${accent}aa` : c.border },
  };
  return (
    <Box sx={{
      minWidth: 0, p: xl ? 1.25 : large ? 1 : 0.75, borderRadius: 1.5,
      bgcolor: set ? `${accent}12` : 'transparent',
      border: `1px dashed ${set ? `${accent}88` : c.border}`,
      transition: 'background-color 140ms, border-color 140ms',
    }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.4 }}>
        <Typography sx={{ color: set ? accent : c.textMuted, fontSize: xl ? '0.8rem' : large ? '0.72rem' : '0.64rem', fontWeight: 800, letterSpacing: 0.2 }} noWrap>
          {label}
        </Typography>
        {set && <CheckCircleIcon sx={{ fontSize: 11, color: accent }} />}
      </Box>
      <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
        <TextField
          size="small"
          type={money ? 'text' : 'number'}
          inputProps={{ inputMode: 'numeric', 'aria-label': `${label} minimum` }}
          placeholder="Min"
          value={value.min}
          onChange={(event) => onChange({ ...value, min: format(event.target.value) })}
          sx={fieldSx}
        />
        <Typography sx={{ color: c.textMuted, fontSize: '0.7rem' }}>–</Typography>
        <TextField
          size="small"
          type={money ? 'text' : 'number'}
          inputProps={{ inputMode: 'numeric', 'aria-label': `${label} maximum` }}
          placeholder="Max"
          value={value.max}
          onChange={(event) => onChange({ ...value, max: format(event.target.value) })}
          sx={fieldSx}
        />
      </Box>
      {presets && presets.length > 0 && (
        <Box sx={{ display: 'flex', gap: 0.4, flexWrap: 'wrap', mt: 0.6 }}>
          {presets.map((preset) => {
            const selected = presetActive(value, preset);
            return (
              <Box
                key={preset.label}
                role="button"
                tabIndex={0}
                aria-pressed={selected}
                onClick={() => onChange(selected ? { min: '', max: '' } : { min: preset.min ?? '', max: preset.max ?? '' })}
                onKeyDown={(event) => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    onChange(selected ? { min: '', max: '' } : { min: preset.min ?? '', max: preset.max ?? '' });
                  }
                }}
                sx={{
                  px: xl ? 1.1 : large ? 0.9 : 0.7, py: xl ? 0.45 : large ? 0.3 : 0.15, borderRadius: 5, cursor: 'pointer', userSelect: 'none',
                  fontSize: xl ? '0.76rem' : large ? '0.68rem' : '0.6rem', fontWeight: 700, whiteSpace: 'nowrap',
                  border: `1px solid ${selected ? accent : c.border}`,
                  bgcolor: selected ? accent : 'transparent',
                  color: selected ? '#fff' : c.textMuted,
                  transition: 'all 120ms',
                  '&:hover': { borderColor: accent, color: selected ? '#fff' : accent },
                }}
              >
                {preset.label}
              </Box>
            );
          })}
        </Box>
      )}
    </Box>
  );
}
